import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, TrendingUp, Users } from "lucide-react";

const BAND_TONE = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/40 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
};

const BAND_ORDER = ["Normal", "Mild", "Moderate", "High"];
const BAND_BAR = {
  Normal: "bg-primary/60",
  Mild: "bg-secondary",
  Moderate: "bg-[hsl(var(--chart-4))]",
  High: "bg-destructive/70",
};

function DistributionBar({ dist, total }) {
  if (!total) {
    return (
      <div className="text-xs text-muted-foreground italic">
        Not enough peers to show a distribution yet.
      </div>
    );
  }
  return (
    <div>
      <div className="h-3 w-full rounded-full overflow-hidden flex" data-testid="cohort-bar">
        {BAND_ORDER.map((b) => {
          const v = dist[b] || 0;
          const pct = (v / total) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={b}
              className={"h-3 " + BAND_BAR[b]}
              style={{ width: `${pct}%` }}
              title={`${b}: ${v} (${pct.toFixed(0)}%)`}
              data-testid={`cohort-bar-${b}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
        {BAND_ORDER.map((b) => (
          <span key={b} className="inline-flex items-center gap-1.5">
            <span className={"inline-block h-2.5 w-2.5 rounded-sm " + BAND_BAR[b]} />
            {b} · {dist[b] || 0}
          </span>
        ))}
      </div>
    </div>
  );
}

const SECTION_LABEL = {
  MENTAL_HEALTH: "Mental Health",
  BRAIN_HEALTH: "Brain Health",
  ADDICTIONS: "Addictions",
  COGNITIVE_HEALTH: "Brain Health",
  INTOXICATION: "Addictions",
  NEURODEGENERATIVE: "Brain Health",
  SUBSTANCE_ABUSE: "Addictions",
};
const prettySection = (s) => SECTION_LABEL[s]
  || (s || "OTHER").replace(/_/g, " ").replace(/\w\S*/g, w => w[0] + w.slice(1).toLowerCase());

function TrendLine({ history, instrumentId }) {
  // history is oldest→newest with {at, total, band}. Renders a compact SVG
  // sparkline with dots + a trailing "Now" label. Only shows when ≥2 points.
  const points = (history || []).filter((p) => typeof p.total === "number");
  if (points.length < 2) {
    return (
      <div className="pt-3 border-t border-border/50 text-xs text-muted-foreground italic"
           data-testid={`insight-trend-empty-${instrumentId}`}>
        Trend appears here after your second submission for this instrument.
      </div>
    );
  }
  const totals = points.map((p) => p.total);
  const min = Math.min(...totals);
  const max = Math.max(...totals);
  const flat = max === min;
  const span = flat ? 1 : max - min;
  const w = 320;
  const h = 60;
  const pad = 8;
  const stepX = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const xy = points.map((p, idx) => {
    const x = pad + stepX * idx;
    // When every total is equal, centre the flat line vertically instead of
    // pinning it to the baseline so users don't misread it as "at the floor".
    const y = flat ? h / 2 : h - pad - ((p.total - min) / span) * (h - pad * 2);
    return { x, y, p };
  });
  const path = xy.map((pt, i) => (i === 0 ? "M" : "L") + `${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(" ");
  const first = points[0].total;
  const last = points[points.length - 1].total;
  const delta = last - first;
  const deltaLabel = delta === 0 ? "no change" : `${delta > 0 ? "+" : ""}${delta}`;

  return (
    <div className="pt-3 border-t border-border/50 space-y-2"
         data-testid={`insight-trend-${instrumentId}`}>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="uppercase tracking-wider">Your history · {points.length} submissions</span>
        <span data-testid={`insight-trend-delta-${instrumentId}`}>
          Since first: <span className="text-foreground font-medium">{deltaLabel}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14"
           preserveAspectRatio="none" role="img" aria-label="Score trend">
        <path d={path} fill="none" strokeWidth="2"
              stroke="hsl(var(--primary))" strokeLinecap="round" strokeLinejoin="round" />
        {xy.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r={i === xy.length - 1 ? 4 : 2.5}
                  fill={i === xy.length - 1 ? "hsl(var(--primary))" : "white"}
                  stroke="hsl(var(--primary))" strokeWidth="1.5">
            <title>{`${pt.p.at?.slice(0, 10) || ""} · total ${pt.p.total} · ${pt.p.band}`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

export default function RespondentInsights() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/insights/mine");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load insights");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    load();
  }, [user, loading, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOptIn = async (next) => {
    setSaving(true);
    try {
      await api.patch("/insights/opt-in", { opt_in: next });
      toast.success(next ? "Cohort insights unlocked" : "Cohort comparison hidden");
      await load();
    } catch {
      toast.error("Could not update preference");
    } finally {
      setSaving(false);
    }
  };

  const bySection = useMemo(() => {
    const map = {};
    (data?.instruments || []).forEach((i) => {
      (map[i.section] = map[i.section] || []).push(i);
    });
    return map;
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container-narrow py-20 text-muted-foreground">Loading insights…</div>
      </div>
    );
  }

  const sections = Object.keys(bySection);
  const belowKAnon = data.opt_in && data.cohort_peer_count < data.k_anon_threshold;

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <button
          onClick={() => nav("/results")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="insights-back-btn"
        >
          <ArrowLeft size={14} /> Back to results
        </button>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/30 px-3 py-1 text-xs text-primary">
            <TrendingUp size={14} /> Your insights
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-4">
            How your scores compare
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            An anonymised look at where your latest screening scores sit relative to other
            respondents in your cohort. This is optional and can be turned off at any time.
          </p>
        </div>

        <Card className="mano-card mb-8" data-testid="insights-consent-card">
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3 max-w-xl">
              <ShieldCheck size={20} className="text-primary mt-0.5" />
              <div>
                <div className="font-medium">Share opt-in for cohort comparison</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Turn this on to see how your scores compare with anonymised peers in your cohort.
                  We only show comparison data when at least {data.k_anon_threshold} other peers exist.
                  No peer is ever individually identified.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground" data-testid="insights-opt-status">
                {data.opt_in ? "On" : "Off"}
              </span>
              <Switch
                checked={data.opt_in}
                disabled={saving}
                onCheckedChange={toggleOptIn}
                data-testid="insights-opt-in-switch"
              />
            </div>
          </CardContent>
        </Card>

        {belowKAnon && (
          <Card className="mano-card mb-8">
            <CardContent className="p-5 text-sm text-muted-foreground flex items-start gap-3">
              <Users size={18} className="mt-0.5 text-primary" />
              <div>
                Cohort comparison is on, but your cohort currently has fewer than
                {" "}{data.k_anon_threshold} other respondents, so peer data is being withheld to
                protect anonymity. We'll show it as soon as more peers complete their screenings.
              </div>
            </CardContent>
          </Card>
        )}

        {sections.length === 0 ? (
          <Card className="mano-card">
            <CardContent className="p-8 text-muted-foreground">
              Complete a questionnaire first — your insights will appear here.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8" data-testid="insights-list">
            {sections.map((section) => (
              <div key={section}>
                <h2 className="font-display text-xl sm:text-2xl tracking-tight mb-4">
                  {prettySection(section)}
                </h2>
                <div className="grid gap-4">
                  {bySection[section].map((i) => (
                    <Card key={i.instrument_id} className="mano-card"
                          data-testid={`insight-card-${i.instrument_id}`}>
                      <CardContent className="p-6 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">
                              {i.instrument_id}
                            </div>
                            <div className="font-display font-semibold text-lg mt-1">{i.title}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={"border " + (BAND_TONE[i.my.band] || "")}
                                   data-testid={`insight-my-band-${i.instrument_id}`}>
                              You · {i.my.band}
                            </Badge>
                            <div className="text-right">
                              <div className="text-xs text-muted-foreground">Your score</div>
                              <div className="font-display text-2xl leading-none"
                                   data-testid={`insight-my-total-${i.instrument_id}`}>
                                {i.my.total}
                              </div>
                            </div>
                          </div>
                        </div>

                        <TrendLine history={i.history} instrumentId={i.instrument_id} />

                        {i.cohort ? (
                          <div className="pt-3 border-t border-border/50 space-y-2"
                               data-testid={`insight-cohort-${i.instrument_id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                Anonymised cohort ({i.cohort.n} peers)
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Avg score:{" "}
                                <span className="font-medium text-foreground"
                                      data-testid={`insight-cohort-avg-${i.instrument_id}`}>
                                  {i.cohort.avg_total ?? "—"}
                                </span>
                              </div>
                            </div>
                            <DistributionBar
                              dist={i.cohort.band_distribution}
                              total={i.cohort.n}
                            />
                          </div>
                        ) : data.opt_in ? null : (
                          <div className="pt-3 border-t border-border/50 text-xs text-muted-foreground italic">
                            Turn on cohort comparison above to see how peers scored.
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 border-t border-border/60 pt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-md">
            All cohort figures are aggregated across at least {data.k_anon_threshold} peers before
            being shown. Individual peer scores are never displayed.
          </p>
          <Button variant="outline" onClick={() => nav("/results")}
                  className="rounded-full px-6" data-testid="insights-results-btn">
            Back to results
          </Button>
        </div>
      </main>
    </div>
  );
}
